import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRealtime } from "@tcg/realtime/react";
import { cn } from "@tcg/ui";
import { HandCoins, History, XCircle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApprovalCancelled, useApproval } from "@/app/approval";
import { useCan, usePos } from "@/app/context";
import { Modal } from "@/components/Modal";
import { NumPad } from "@/components/NumPad";
import { useToast } from "@/components/Toast";
import { call } from "@/lib/api";
import type { IdentityCheckCreated } from "@/lib/consignment-types";
import { readSetting, writeSetting } from "@/lib/db";
import { errorText, isApiError } from "@/lib/errors";
import { newIdempotencyKey } from "@/lib/idempotency";
import { POS_TOPICS } from "@/lib/sse";
import type { Buylist, BuylistLine, BuylistLineAdd, BuylistCreate } from "@/lib/types";

import { BuylistDone, printKaitori } from "./DoneScreen";
import { IdentityCheckModal } from "./IdentityCheckModal";
import { buylistListKey, IntakeHistory } from "./IntakeHistory";
import { acceptBlockers, INITIAL_PAYOUT, PayoutPanel, type PayoutState } from "./PayoutPanel";
import { QuoteLineForm, QuoteLineRow } from "./QuoteLine";
import { IdentityBar, SellerPicker, type IdentityStatus } from "./SellerPanel";

export const CURRENT_KEY = "buylistCurrent";
const identityKey = (intakeId: string) => `buylistIdentity:${intakeId}`;
const intakeKey = (id: string | null) => ["pos-buylist", "detail", id] as const;

interface LocalIdentity {
  id: string;
  expiresAt: string | null;
}

export function identityStatusOf(
  intake: Pick<Buylist, "identityCheck" | "identityCheckId">,
  local: LocalIdentity | null,
  now = Date.now(),
): { status: IdentityStatus; id: string | null; expiresAt: string | null } {
  if (local) {
    const expired = local.expiresAt !== null && Date.parse(local.expiresAt) <= now;
    return { status: expired ? "expired" : "valid", id: local.id, expiresAt: local.expiresAt };
  }
  const gate = intake.identityCheck;
  const id = intake.identityCheckId ?? gate.existingId ?? null;
  return { status: gate.status, id: gate.status === "valid" ? id : null, expiresAt: null };
}

function sellerLabel(intake: Buylist): string {
  const seller = intake.seller as Record<string, unknown>;
  const name = seller.name ?? seller.fullName ?? seller.displayName;
  return typeof name === "string" && name ? name : "—";
}

/**
 * 買取 (T07-31, design/05 §3.4): the seller → 本人確認 (photo + the 古物台帳 fields — the system
 * says when it is needed and blocks a minor by itself) → the cards with Rank A–D (raw) / grade →
 * 合計 + how they are paid → 「買取確定・支払」 (pos-service: 古物台帳, draft products, payout, event)
 * → 完了 (the drawer opens for cash, 買取明細書 ×2, labels). Everything amount-related is the
 * server's; the history of the shift is on the second tab.
 */
export function BuylistScreen() {
  const { t } = useTranslation();
  const { api, db, printer } = usePos();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { ask, withApproval } = useApproval();
  const canOverLimit = useCan("buylist.accept_over_limit");
  const [tab, setTab] = useState<"buy" | "history">("buy");
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [local, setLocal] = useState<LocalIdentity | null>(null);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [payout, setPayout] = useState<PayoutState>(INITIAL_PAYOUT);
  const [busy, setBusy] = useState(false);
  const [lineError, setLineError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ line: BuylistLine; price: string } | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [done, setDone] = useState<Buylist | null>(null);
  const acceptKey = useRef<string | null>(null);

  useEffect(() => {
    void readSetting<string | null>(db, CURRENT_KEY, null).then(async (id) => {
      setCurrentId(id);
      if (id) {
        setLocal(await readSetting<LocalIdentity | null>(db, identityKey(id), null));
      }
      setLoaded(true);
    });
  }, [db]);

  const intake = useQuery({
    queryKey: intakeKey(currentId),
    queryFn: () =>
      call(() =>
        api.GET("/v1/pos/buylist-intakes/{intake_id}", {
          params: { path: { intake_id: currentId ?? "" } },
        }),
      ),
    enabled: currentId !== null,
  });
  useRealtime([POS_TOPICS.buylist], () => void intake.refetch(), {
    enabled: currentId !== null,
    onResync: () => void intake.refetch(),
  });

  // a purchase that is no longer open (accepted on another tab, rejected …) is forgotten
  const stale =
    intake.data !== undefined &&
    intake.data.status !== "draft" &&
    intake.data.status !== "offered" &&
    !done;
  useEffect(() => {
    if (stale) {
      void writeSetting(db, CURRENT_KEY, null);
    }
  }, [db, stale]);

  const setIntake = useCallback(
    (data: Buylist) => queryClient.setQueryData(intakeKey(data.id), data),
    [queryClient],
  );

  const reset = async () => {
    await writeSetting(db, CURRENT_KEY, null);
    setCurrentId(null);
    setLocal(null);
    setPayout(INITIAL_PAYOUT);
    setDone(null);
    setError(null);
    setLineError(null);
    acceptKey.current = null;
    void queryClient.invalidateQueries({ queryKey: ["pos-buylist", "list"] });
  };

  const start = async (body: BuylistCreate) => {
    setBusy(true);
    setError(null);
    try {
      const created = await call(() => api.POST("/v1/pos/buylist-intakes", { body }));
      setIntake(created);
      await writeSetting(db, CURRENT_KEY, created.id);
      setCurrentId(created.id);
      setLocal(null);
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const addLine = async (body: BuylistLineAdd): Promise<boolean> => {
    if (!currentId) {
      return false;
    }
    setBusy(true);
    setLineError(null);
    try {
      const updated = await call(() =>
        api.POST("/v1/pos/buylist-intakes/{intake_id}/lines", {
          params: { path: { intake_id: currentId } },
          body,
        }),
      );
      setIntake(updated);
      return true;
    } catch (err) {
      setLineError(errorText(err, t));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const savePrice = async () => {
    if (!currentId || !editing) {
      return;
    }
    setBusy(true);
    try {
      const updated = await call(() =>
        api.PATCH("/v1/pos/buylist-intakes/{intake_id}/lines/{line_id}", {
          params: { path: { intake_id: currentId, line_id: editing.line.id } },
          body: { offerPrice: Number.parseInt(editing.price || "0", 10) },
        }),
      );
      setIntake(updated);
      setEditing(null);
    } catch (err) {
      toast.show(errorText(err, t), "error");
    } finally {
      setBusy(false);
    }
  };

  const removeLine = async (line: BuylistLine) => {
    if (!currentId) {
      return;
    }
    try {
      const updated = await call(() =>
        api.DELETE("/v1/pos/buylist-intakes/{intake_id}/lines/{line_id}", {
          params: { path: { intake_id: currentId, line_id: line.id } },
        }),
      );
      setIntake(updated);
    } catch (err) {
      toast.show(errorText(err, t), "error");
    }
  };

  const offer = async (): Promise<Buylist | null> => {
    if (!currentId) {
      return null;
    }
    const offered = await call(() =>
      api.POST("/v1/pos/buylist-intakes/{intake_id}/offer", {
        params: { path: { intake_id: currentId } },
      }),
    );
    setIntake(offered);
    return offered;
  };

  const printOffer = async () => {
    setBusy(true);
    setError(null);
    try {
      const offered = await offer();
      if (offered) {
        const ok = await printKaitori({ api, printer }, offered, [t("buylist.copy_offer")]);
        toast.show(
          ok ? t("buylist.offer_printed") : t("buylist.print_failed"),
          ok ? "success" : "error",
        );
      }
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    const data = intake.data;
    if (!data) {
      return;
    }
    try {
      const token = await ask({
        permission: "buylist.accept_over_limit",
        action: "buylist.accept",
        subject: data.id,
        amount: data.totalOffer,
        reason: t("buylist.approve_reason"),
      });
      setPayout((p) => ({ ...p, approvalToken: token }));
    } catch (err) {
      if (!(err instanceof ApprovalCancelled)) {
        throw err;
      }
    }
  };

  const accept = async () => {
    const data = intake.data;
    if (!data) {
      return;
    }
    const identity = identityStatusOf(data, local);
    setBusy(true);
    setError(null);
    try {
      let current = data;
      if (current.status === "draft") {
        const offered = await offer();
        current = offered ?? current;
        if (current.approval?.overLimit && !canOverLimit && !payout.approvalToken) {
          return; // the banner + 「店長承認」 are shown now
        }
      }
      acceptKey.current ??= newIdempotencyKey();
      const key = acceptKey.current;
      const body = {
        ...(identity.id ? { identityCheckId: identity.id } : {}),
        payoutMethod: payout.method,
        storeCreditBonusPercent:
          payout.method === "store_credit"
            ? (current.payoutOptions?.storeCredit.bonusPercent ?? 0)
            : 0,
        sellerTaxStatus: payout.hasTNumber
          ? { hasRegistrationNumber: true, registrationNumber: payout.tNumber.trim() }
          : { hasRegistrationNumber: false },
        taxFreeItemCheck: payout.taxFreeCheck,
        ...(current.customerId ? { customerId: current.customerId } : {}),
      };
      const send = (approvalToken?: string) =>
        call(() =>
          api.POST("/v1/pos/buylist-intakes/{intake_id}/accept", {
            params: { path: { intake_id: current.id } },
            body,
            headers: {
              "Idempotency-Key": key,
              ...((approvalToken ?? payout.approvalToken)
                ? { "X-Approval-Token": approvalToken ?? payout.approvalToken ?? "" }
                : {}),
            },
          }),
        );
      let result: Buylist;
      try {
        result = await withApproval(send, { subject: current.id, amount: current.totalOffer });
      } catch (err) {
        if (isApiError(err, "buylist_limit_exceeded")) {
          const token = await ask({
            permission: "buylist.accept_over_limit",
            action: "buylist.accept",
            subject: current.id,
            amount: current.totalOffer,
            reason: t("buylist.approve_reason"),
          });
          result = await send(token);
        } else {
          throw err;
        }
      }
      setDone(result);
      await writeSetting(db, CURRENT_KEY, null);
      void queryClient.invalidateQueries({ queryKey: buylistListKey(result.shiftId ?? null) });
    } catch (err) {
      if (err instanceof ApprovalCancelled) {
        return;
      }
      // a refusal is final for that key; a lost answer is retried with the same key
      if (!isApiError(err, "network") && !(isApiError(err) && err.status >= 500)) {
        acceptKey.current = null;
      }
      if (isApiError(err, "identity_check_expired")) {
        setLocal(null);
      }
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  const reject = async (reason: string) => {
    if (!currentId) {
      return;
    }
    setBusy(true);
    try {
      await call(() =>
        api.POST("/v1/pos/buylist-intakes/{intake_id}/reject", {
          params: { path: { intake_id: currentId } },
          body: { reason },
        }),
      );
      setRejecting(false);
      await reset();
    } catch (err) {
      toast.show(errorText(err, t), "error");
    } finally {
      setBusy(false);
    }
  };

  const onIdentity = async (check: IdentityCheckCreated) => {
    if (!currentId) {
      return;
    }
    const record = { id: check.identityCheckId, expiresAt: check.expiresAt ?? null };
    await writeSetting(db, identityKey(currentId), record);
    setLocal(record);
    setIdentityOpen(false);
  };

  if (done) {
    return <BuylistDone intake={done} onNext={() => void reset()} />;
  }

  const data = currentId && !stale ? intake.data : undefined;
  const identity = data ? identityStatusOf(data, local) : null;
  const editable = data ? data.status === "draft" || data.status === "offered" : false;
  const blockers =
    data && identity
      ? acceptBlockers({
          intake: data,
          identity: identity.status,
          payout,
          canApproveOverLimit: canOverLimit,
        })
      : [];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex gap-2 border-b px-3 py-2">
        <div role="tablist" aria-label={t("nav.buylist")} className="flex gap-2">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "buy"}
            onClick={() => setTab("buy")}
            className={cn(
              "flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg",
              tab === "buy" && "border-primary bg-primary text-primary-foreground",
            )}
          >
            <HandCoins aria-hidden className="size-5" />
            {t("buylist.tab_buy")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "history"}
            onClick={() => setTab("history")}
            className={cn(
              "flex min-h-pos-touch items-center gap-2 rounded-md border px-5 text-lg",
              tab === "history" && "border-primary bg-primary text-primary-foreground",
            )}
          >
            <History aria-hidden className="size-5" />
            {t("buylist.tab_history")}
          </button>
        </div>
        {data && editable ? (
          <button
            type="button"
            onClick={() => setRejecting(true)}
            className="ml-auto flex min-h-pos-touch items-center gap-2 rounded-md border border-status-problem px-5 text-lg text-pos-problem"
          >
            <XCircle aria-hidden className="size-5" />
            {t("buylist.reject")}
          </button>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {tab === "history" ? (
          <IntakeHistory />
        ) : !loaded ? null : !data || !identity ? (
          <div className="mx-auto flex max-w-3xl flex-col gap-3">
            <h1 className="text-2xl font-semibold">{t("nav.buylist")}</h1>
            <SellerPicker onChoose={(choice) => void start(choice.body)} busy={busy} />
            {error ? (
              <p role="alert" className="text-pos-problem">
                {error}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_22rem] xl:grid-cols-[minmax(0,1fr)_26rem]">
            <div className="flex min-w-0 flex-col gap-3">
              <IdentityBar
                seller={sellerLabel(data)}
                status={identity.status}
                expiresAt={identity.expiresAt}
                legalMandatory={data.identityCheck.legalMandatory}
                onCheck={() => setIdentityOpen(true)}
                disabled={busy}
              />
              <ul aria-label={t("buylist.lines")} className="rounded-lg border">
                {data.lines.map((line) => (
                  <QuoteLineRow
                    key={line.id}
                    line={line}
                    editable={editable}
                    onEditPrice={(l) => setEditing({ line: l, price: String(l.offerPrice) })}
                    onRemove={(l) => void removeLine(l)}
                  />
                ))}
                {data.lines.length === 0 ? (
                  <li className="p-3 text-muted-foreground">{t("buylist.no_lines")}</li>
                ) : null}
              </ul>
              {editable ? <QuoteLineForm busy={busy} onAdd={addLine} error={lineError} /> : null}
            </div>
            <div className="flex flex-col gap-3">
              <PayoutPanel
                intake={data}
                payout={payout}
                onChange={(changes) => setPayout((p) => ({ ...p, ...changes }))}
                blockers={blockers}
                busy={busy}
                onOffer={() => void printOffer()}
                onAccept={() => void accept()}
                onApprove={() => void approve()}
                canApproveOverLimit={canOverLimit}
              />
              {error ? (
                <p role="alert" className="text-pos-problem">
                  {error}
                </p>
              ) : null}
            </div>
          </div>
        )}
      </div>
      {data ? (
        <IdentityCheckModal
          open={identityOpen}
          onClose={() => setIdentityOpen(false)}
          onDone={(check) => void onIdentity(check)}
          customerId={data.customerId ?? null}
          amount={data.totalOffer}
          defaultName={sellerLabel(data) === "—" ? null : sellerLabel(data)}
        />
      ) : null}
      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        busy={busy}
        title={t("buylist.edit_price_title")}
        description={editing ? (editing.line.title.ja ?? editing.line.title.en ?? "") : ""}
      >
        {editing ? (
          <NumPad
            label={t("buylist.offer_price")}
            value={editing.price}
            onChange={(price) => setEditing({ ...editing, price })}
            money
            onEnter={() => void savePrice()}
            enterLabel={t("settings.save")}
            disabled={busy}
          />
        ) : null}
      </Modal>
      <RejectModal
        open={rejecting}
        busy={busy}
        onClose={() => setRejecting(false)}
        onReject={(r) => void reject(r)}
      />
    </div>
  );
}

const REJECT_REASONS = ["price", "changed_mind", "no_identity", "other"] as const;

function RejectModal({
  open,
  busy,
  onClose,
  onReject,
}: {
  open: boolean;
  busy: boolean;
  onClose: () => void;
  onReject: (reason: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={busy}
      title={t("buylist.reject_title")}
      description={t("buylist.reject_body")}
    >
      <div className="grid gap-2">
        {REJECT_REASONS.map((reason) => (
          <button
            key={reason}
            type="button"
            disabled={busy}
            onClick={() => onReject(t(`buylist.reject_${reason}`, { lng: "ja" }))}
            className="min-h-pos-touch rounded-md border px-4 text-left text-lg"
          >
            {t(`buylist.reject_${reason}`)}
          </button>
        ))}
      </div>
    </Modal>
  );
}
